export interface JumpPillBounds {
  requiredScore: number
  /** Document-space px. */
  top: number
  /** Document-space px. */
  bottom: number
}

export interface JumpScrollContext {
  /**
   * The unobstructed viewport band pills should land in, viewport-space px
   * (the bottom excludes the fixed final-rewards footer).
   */
  bandTop: number
  bandBottom: number
  viewportHeight: number
  /** Full document scroll height. */
  scrollHeight: number
}

/**
 * Target scrollTop for a Jump anchor tap: center the region covering every
 * pill in the unobstructed band when it fits; otherwise center the nearest
 * point beyond the user's progress (pills are purchasable ⇒ all beyond
 * progress ⇒ the lowest score). Clamped to the document's scrollable
 * range; null when there is nothing to scroll to.
 */
export function getJumpAnchorScrollTarget(
  pills: readonly JumpPillBounds[],
  context: JumpScrollContext,
): number | null {
  if (pills.length === 0) return null
  const { bandTop, bandBottom, viewportHeight, scrollHeight } = context
  const regionTop = Math.min(...pills.map((pill) => pill.top))
  const regionBottom = Math.max(...pills.map((pill) => pill.bottom))
  const fitsTogether = regionBottom - regionTop <= bandBottom - bandTop
  const nearest = pills.reduce((lowest, pill) =>
    pill.requiredScore < lowest.requiredScore ? pill : lowest,
  )
  const focusCenter = fitsTogether
    ? (regionTop + regionBottom) / 2
    : (nearest.top + nearest.bottom) / 2
  const target = focusCenter - (bandTop + bandBottom) / 2
  const maxScroll = Math.max(0, scrollHeight - viewportHeight)
  return Math.min(Math.max(target, 0), maxScroll)
}
